/**
 * Houseplant v2 — Mobile menu drawer
 *
 * One job: hand off from the drawer to the header's predictive-search panel.
 *
 * There is no close-button handler here any more. The panel used to cover the
 * header and draw its own close X, which needed wiring; it now sits BELOW the
 * header, so the <summary> that opened it is still on screen and still the
 * thing that closes it — the base theme's own toggle, no code of ours.
 */

/**
 * Search: hand off from the drawer to the header's predictive-search panel.
 *
 * The row is a real <a href="/search"> in the markup, so it works without this
 * script and behaves like a link for middle-click and "open in new tab". What
 * it CANNOT express is the actual behaviour: close this drawer, then open a
 * different component. That is two actions on two elements, so it is done here.
 *
 * ── The order matters, and so does the wait ────────────────────────────────
 * The panel's trigger is a <summary> inside the header, which the drawer is
 * covering. Clicking it while the drawer is still open would open the panel
 * behind the drawer, and `<menu-drawer>`'s own `focusout` handler would then
 * fight it — focus lands in the panel, the drawer sees focus leave, and closes
 * itself, which yanks focus back to the hamburger.
 *
 * So the drawer is closed first and the panel opened on the `menudrawer:close`
 * event the base dispatches at the end of its close path. One listener, bound
 * per click and removed once it fires, rather than a timeout guessing at the
 * animation.
 */
document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-hp-menu-search]');
  if (!link) return;

  /* Let modified clicks behave like the link they are. */
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;

  const drawer = link.closest('header-drawer, menu-drawer');
  const trigger = document.querySelector('search-modal summary');
  if (!drawer || !trigger) return; /* no panel on this page — follow the href */

  event.preventDefault();

  const openSearch = () => {
    document.removeEventListener('menudrawer:close', openSearch);
    trigger.click();
  };
  document.addEventListener('menudrawer:close', openSearch);

  drawer.dispatchEvent(new CustomEvent('drawer:force-close', { bubbles: false }));
});

/**
 * The navigation tree's expand/collapse.
 *
 * ── WHY THIS IS NOT A <details> ────────────────────────────────────────────
 * `<menu-drawer>` in global.js binds EVERY `summary` inside itself and treats a
 * nested one as a sliding sub-panel: `onSummaryClick` adds `menu-opening` and
 * then calls `trapFocus` on the summary's next sibling. That is right for a
 * panel that covers the menu and wrong for a section that expands inside it —
 * the keyboard would be pinned inside the open group with no way to reach the
 * next top-level item. Base binds in its constructor, so beating it with
 * `stopImmediatePropagation` would depend on script order; a <button> is simply
 * not a `summary`, so there is nothing to race. The drawer's own <details> is
 * untouched and still runs the whole open/close/escape/focus machine.
 *
 * ── NO HEIGHT ANIMATION, DELIBERATELY ──────────────────────────────────────
 * The theme's other accordion (`.hp-accordion`, shared by the PDP and the FAQ)
 * shows and hides its body with no transition, and this matches it rather than
 * inventing a second convention. It also sidesteps the expensive one: height
 * and max-height are not composited, so animating them repaints every frame on
 * the main thread — the thing CLAUDE.md's motion note rules out. The chevron
 * still turns, because `transform` is free.
 *
 * `hidden` rather than a class: it is the accessible state as well as the
 * visual one, so the collapsed content is out of the tab order and out of the
 * accessibility tree without a second rule having to remember to do that.
 */
document.addEventListener('click', (event) => {
  const toggle = event.target.closest('[data-hp-menu-toggle]');
  if (!toggle) return;

  const panel = document.getElementById(toggle.getAttribute('aria-controls'));
  if (!panel) return;

  const isOpen = toggle.getAttribute('aria-expanded') === 'true';
  toggle.setAttribute('aria-expanded', String(!isOpen));
  panel.hidden = isOpen;
});

/**
 * Collapse everything when the drawer closes.
 *
 * Without this the menu remembers its last shape, so reopening it can land the
 * visitor part-way down an expanded group with the top-level items scrolled off
 * — the drawer reopens looking like a different menu. `menudrawer:close` is the
 * event the base dispatches at the end of its own close path, which is the same
 * hook the search and account hand-offs above already use.
 */
document.addEventListener('menudrawer:close', () => {
  document.querySelectorAll('[data-hp-menu-toggle][aria-expanded="true"]').forEach((toggle) => {
    toggle.setAttribute('aria-expanded', 'false');
    const panel = document.getElementById(toggle.getAttribute('aria-controls'));
    if (panel) panel.hidden = true;
  });
});
